import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { TAGLINE } from "@/components/Brand";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Flame, Crown, Gem, Check, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FounderBadge } from "@/components/FounderBadge";
import { PreCheckoutAuthModal } from "@/components/PreCheckoutAuthModal";

const founderPerks = [
  "All Diamond tier benefits",
  "Unlimited active conversation slots & Reverses",
  '10 free "Skip the Chat" video calls/mo, 15 Ignites/mo',
  "Exclusive Founding Member profile badge",
  "Limited availability per launch corridor",
];

const consumables = [
  { name: 'Skip the Chat (5-Min Live Video Calls)', packs: [["1 Call", "$2.99"], ["3 Calls", "$6.99"]] },
  { name: "Profile Boosts", packs: [["1 Boost", "$2.99"], ["5 Boosts", "$9.99"], ["10 Boosts", "$16.99"]] },
  { name: "Ignites", packs: [["3 Ignites", "$2.99"], ["7 Ignites", "$9.99"], ["12 Ignites", "$14.99"]] },
  { name: "Roses / Direct Sparks", packs: [["1 Rose", "$2.99"], ["6 Roses", "$11.99"], ["12 Roses", "$19.99"]] },
  { name: "Profile Reverses / Rewinds", packs: [["3 Reverses", "$2.99"], ["8 Reverses", "$5.99"]] },
  { name: "Stashes", packs: [["1 Stash", "$1.99"], ["5 Stashes", "$5.99"], ["12 Stashes", "$17.99"]] },
];

const plans = [
  {
    key: "gold_monthly",
    name: "Gold",
    price: "$14.99",
    period: "/mo",
    accent: "#F59E0B",
    annual: { key: "gold_annual", label: "or $99.00/yr" },
    perks: ["Unlimited swiping", "5 active conversation slots", "See who liked you (unblurred tray)", "1 free Profile Boost/mo", "3 free Ignites/mo", "5 free Reverses/mo", "48h VIP Guest Passes for squad nights"],
  },
  {
    key: "platinum_monthly",
    name: "Platinum",
    price: "$29.99",
    period: "/mo",
    accent: "#E11D48",
    perks: ["Everything in Gold", "10 active conversation slots", "2 free Profile Boosts/mo", "7 free Ignites/mo", "10 free Reverses/mo", "5 Pre-Match Notes/day (140 chars on clips)", "Co-Op split-screen video recording", "20% off consumable packs"],
  },
  {
    key: "diamond_monthly",
    name: "Diamond",
    price: "$49.99",
    period: "/mo",
    accent: "#BE123C",
    hero: "Over $115 in monthly perks included",
    perks: ["Everything in Platinum", "Unlimited active conversation slots", '10 free "Skip the Chat" 5-min live video calls/mo', "4 free Priority Profile Boosts/mo", "15 free Ignites/mo", "Unlimited Reverses (∞)", "35% off consumable packs", "Direct Founder VIP feedback channel"],
    featured: true,
  },
];

export default function Join() {
  const [loading, setLoading] = useState(null);
  const [founder, setFounder] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/founder/stats").then(({ data }) => setFounder(data)).catch(() => setFounder(null));
  }, []);

  const TIER_LABELS = {
    founder_pass: "the Founder Pass",
    diamond_monthly: "Diamond",
    platinum_monthly: "Platinum",
    gold_monthly: "Gold",
    gold_annual: "Gold (Annual)",
  };

  // Step 1: open account-setup modal before touching Stripe.
  const startCheckout = (lookup_key) => {
    setPending({ lookup_key, label: TIER_LABELS[lookup_key] });
    setAuthOpen(true);
  };

  // Step 2: account created/verified -> create Stripe session with identity attached.
  const handleAuthed = async ({ user_id, email }) => {
    setAuthOpen(false);
    if (!pending) return;
    const lookup_key = pending.lookup_key;
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id,
        email,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout could not start");
      setLoading(null);
    }
  };

  const claimed = founder?.claimed ?? 0;
  const total = founder?.total ?? 100;
  const pct = Math.min(Math.round((claimed / total) * 100), 100);
  const soldOut = founder?.sold_out;

  return (
    <PublicLayout>
      <section className="relative overflow-hidden spark-grid-bg">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[380px] rounded-full spark-glow-pulse"
          style={{ background: "radial-gradient(circle, rgba(225,29,72,0.18), transparent 70%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#F43F5E]">
              <Flame className="w-3.5 h-3.5" /> Memberships
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="join-title">
              Pick your <span className="spark-gradient-text">Sparkd</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-400">{TAGLINE}</p>
          </div>

          {/* Hero Founder Card */}
          <div
            className="relative mt-12 rounded-2xl bg-[#1F242D] p-8 sm:p-10 overflow-hidden spark-fade-up"
            style={{ border: "2px solid #E11D48", boxShadow: "0 0 48px rgba(225,29,72,0.25)" }}
            data-testid="founder-card"
          >
            <div className="absolute top-0 right-0 w-56 h-56 rounded-full spark-glow-pulse" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.18), transparent 70%)" }} />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <FounderBadge claimedCount={claimed} />
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-heading text-5xl sm:text-6xl font-extrabold text-white">$99</span>
                  <span className="mb-2 text-sm text-slate-400">/yr</span>
                </div>
                <p className="mt-2 text-sm text-slate-400">
                  Founder Annual Pass. All Diamond benefits (<span className="text-white font-semibold">$49.99/mo</span> value).
                </p>

                {/* Inventory bar */}
                <div className="mt-6">
                  <div className="flex items-center justify-between text-xs font-semibold mb-2">
                    <span className="inline-flex items-center gap-1.5 text-[#F59E0B]"><Zap className="w-3.5 h-3.5" /> Limited to First 100 Members Only</span>
                    <span className="text-slate-400" data-testid="founder-count">{claimed}/{total} claimed</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-[#0F172A] overflow-hidden border border-white/[0.06]">
                    <div className="h-full spark-gradient transition-all duration-700" style={{ width: `${Math.max(pct, 3)}%` }} />
                  </div>
                </div>

                <Button
                  onClick={() => startCheckout("founder_pass")}
                  disabled={loading !== null || soldOut}
                  data-testid="founder-checkout-btn"
                  className="mt-7 w-full sm:w-auto px-8 rounded-2xl py-6 text-base font-semibold text-white spark-gradient spark-gradient-hover transition-all"
                >
                  {soldOut ? "Founder Spots Sold Out" : loading === "founder_pass" ? "Starting checkout…" : <>Claim $99 Founder Pass <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>

              <ul className="space-y-3 lg:border-l lg:border-white/[0.08] lg:pl-8">
                {founderPerks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-200">
                    <Check className="w-4 h-4 mt-0.5 text-[#F59E0B] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Standard plans */}
          <div className="mt-14">
            <h2 className="text-center font-heading text-2xl font-bold text-white">Or go monthly</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {plans.map((plan, i) => (
                <div
                  key={plan.key}
                  className={`relative rounded-2xl p-7 bg-[#1F242D] spark-fade-up ${plan.featured ? "border-2 border-[#E11D48]" : "border border-white/[0.08]"}`}
                  style={{ animationDelay: `${i * 70}ms` }}
                  data-testid={`plan-${plan.name.toLowerCase()}`}
                >
                  {plan.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full spark-gradient text-[10px] font-bold text-white uppercase tracking-wide">
                      Most Popular
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Gem className="w-4 h-4" style={{ color: plan.accent }} />
                    <h3 className="font-heading text-lg font-bold text-white">{plan.name}</h3>
                  </div>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-heading text-4xl font-extrabold text-white">{plan.price}</span>
                    <span className="mb-1.5 text-sm text-slate-400">{plan.period}</span>
                  </div>
                  {plan.hero && (
                    <p className="mt-2 inline-block px-2.5 py-1 rounded-full bg-[#E11D48]/15 text-xs font-semibold text-[#F43F5E]" data-testid="diamond-hero-badge">{plan.hero}</p>
                  )}
                  <ul className="mt-5 space-y-2.5">
                    {plan.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: plan.accent }} /> {p}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => startCheckout(plan.key)}
                    disabled={loading !== null}
                    data-testid={`plan-checkout-${plan.name.toLowerCase()}`}
                    className={`mt-7 w-full rounded-2xl py-5 text-sm font-semibold transition-all ${plan.featured ? "text-white spark-gradient spark-gradient-hover" : "text-white bg-[#262D38] border border-white/[0.12] hover:bg-[#2f3744]"}`}
                  >
                    {loading === plan.key ? "Starting…" : `Choose ${plan.name}`}
                  </Button>
                  {plan.annual && (
                    <button
                      onClick={() => startCheckout(plan.annual.key)}
                      disabled={loading !== null}
                      data-testid={`plan-checkout-${plan.name.toLowerCase()}-annual`}
                      className="mt-3 w-full text-xs font-semibold text-slate-400 hover:text-[#F59E0B] transition-colors"
                    >
                      {loading === plan.annual.key ? "Starting…" : `${plan.annual.label} — billed yearly`}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14" data-testid="consumables-section">
            <h2 className="text-center font-heading text-2xl font-bold text-white">Consumable packs</h2>
            <p className="mt-2 text-center text-sm text-slate-400">Available in the Sparkd app. Platinum saves 20%, Diamond saves 35%.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {consumables.map((c) => (
                <div key={c.name} className="rounded-2xl p-5 bg-[#1F242D] border border-white/[0.08]">
                  <h3 className="font-heading text-sm font-bold text-white">{c.name}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {c.packs.map(([q, p]) => (
                      <li key={q} className="flex justify-between text-sm text-slate-300"><span>{q}</span><span className="font-semibold text-white">{p}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Link to="/download" className="text-sm font-semibold text-[#F43F5E] hover:text-[#F59E0B] transition-colors" data-testid="consumables-download-link">Get packs in the app →</Link>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/download" className="text-sm text-slate-400 hover:text-[#F43F5E] transition-colors" data-testid="join-free-link">
              Continue with basic free account →
            </Link>
            <p className="mt-4 text-xs text-slate-500">Secure checkout by Stripe. Cancel anytime. Prices in USD.</p>
          </div>
        </div>
      </section>

      <PreCheckoutAuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        tierLabel={pending?.label}
        onAuthed={handleAuthed}
      />
    </PublicLayout>
  );
}
